import { DeclaredStrength } from '@cf-shared/data-model/user-state';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import { IntakeStep } from './intake-form-interface';
// types only: exercise-db.service.ts pulls in the Angular services, which a unit test cannot load
import type { Exercise, ExerciseSlot, StrengthStandard, StrengthStandard_Relative } from 'services/exercise-db.service';

// The intake form's Strength step: which lifts it asks about, and how the
// user's saved answers are kept in line with them.
//
// Each strength-testing slot (bench press, chin-up, overhead press, RDL, squat)
// lists the exercises a user may test it with. The page reads every one of them
// from the curated catalog, then brings the user's saved `declaredStrength` in
// line with what it found. Kept out of the page so the reading and the clean-up
// can be tested without the page.
//
// A read that FAILS is not the same as an exercise that is not in the catalog.
// The clean-up drops a lift whose exercises are gone and resets a lift whose
// chosen exercise is gone, so taking a failed read (a fetch that timed out) for a
// missing exercise erased the user's own numbers (#6540). A failed read is
// therefore retried once, and if it still fails the whole load is given up and
// the saved answers are left exactly as they are. The page then tells the user
// the step needs a working connection and does not let them pass it.

/** the same test as `isStrengthStandardRelative` in exercise-db.service.ts, which this module cannot import (see above) */
function isRelativeStandard(s: StrengthStandard): s is StrengthStandard_Relative {
  return !('men' in s);
}

export class StrengthTestingUI_Exercise {
  exercise: Exercise;
}

export class StrengthTestingUI_Slot {
  idSlot: string;
  slotName: string;
  slot: ExerciseSlot;
  exercises: StrengthTestingUI_Exercise[];
}

/** what the loader needs from ExerciseDbService */
export interface StrengthTestingExerciseReader {
  /** `undefined` means "not in the catalog"; a read that failed is thrown */
  getGlobalExerciseByIdOrThrowAsync(id: string, watcher: unknown): Promise<Exercise | undefined>;
}

/** what the loader needs from LoggingService */
export interface StrengthTestingProblemReporter {
  trackSilentProblem(problemName: string, context: any): void;
}

export enum StrengthTestsLoad {
  /** every exercise was read; the slots list what the catalog holds */
  Loaded = 'loaded',
  /** a read failed twice; the slots are empty and the user's saved answers were left alone */
  ReadFailed = 'read-failed',
}

/**
 * Whether the Strength step's "tests not loaded" check refuses Next (#6540).
 *
 * Only in a new intake: a review in tabs is not held up, because a failed load leaves the
 * saved answers untouched. And only while the user is at or before the Strength step: once
 * they skipped past it, an error here would make the step progress send them back to it
 * the next time the page loads.
 */
export function strengthLoadBlocksNext(state: { newIntake: boolean, load: StrengthTestsLoad, intakeProgress: number }): boolean {
  return state.newIntake
    && state.load === StrengthTestsLoad.ReadFailed
    && state.intakeProgress <= IntakeStep.Strength;
}

export interface StrengthTestingLoadResult {
  status: StrengthTestsLoad;
  /** the slots that have at least one usable exercise, in the order the catalog gives them */
  slots: StrengthTestingUI_Slot[];
  /** every exercise the step can show, plus the exercises their strength standards are relative to */
  exercises: Exercise[];
}

/**
 * Read the Strength step's exercises, then bring the user's saved answers in `declared`
 * in line with them (in place). This is what the intake page runs on entry.
 */
export async function prepareStrengthStepAsync(
  testingSlots: ExerciseSlot[],
  declared: DeclaredStrength,
  reader: StrengthTestingExerciseReader,
  reporter: StrengthTestingProblemReporter,
  watcher: unknown,
): Promise<StrengthTestingLoadResult> {
  const loaded = await loadStrengthTestingAsync(testingSlots, reader, reporter, watcher);
  // if any read failed, the slots are incomplete, so cleaning the saved answers up to
  // match them would erase answers whose exercises still exist
  if (loaded.status === StrengthTestsLoad.Loaded) {
    reconcileDeclaredStrength(declared, loaded.slots);
  }
  return loaded;
}

/** the load could not read one exercise, even on the second try */
class StrengthTestReadFailed extends Error {
  constructor(readonly exerciseId: string, readonly reason: unknown) {
    super(`Could not read strength-test exercise ${exerciseId}`, { cause: reason });
  }
}

/** read one exercise, trying a second time if the first read fails */
async function readWithRetryAsync(reader: StrengthTestingExerciseReader, id: string, watcher: unknown): Promise<Exercise | undefined> {
  try {
    return await reader.getGlobalExerciseByIdOrThrowAsync(id, watcher);
  } catch (first) {
    // a database that is shutting down (sign-out, teardown) is an expected state, not a
    // failed read: let it travel unreported, as it did before #6540
    if (isDatabaseTornDown(first)) {
      throw first;
    }
    console.log(`Strength-test exercise ${id}: read failed, trying again`, describeErrorForLog(first));
    try {
      return await reader.getGlobalExerciseByIdOrThrowAsync(id, watcher);
    } catch (second) {
      if (isDatabaseTornDown(second)) {
        throw second;
      }
      throw new StrengthTestReadFailed(id, second);
    }
  }
}

/** read every test exercise; a read that failed twice ends the load as `ReadFailed`, reported once */
async function loadStrengthTestingAsync(
  testingSlots: ExerciseSlot[],
  reader: StrengthTestingExerciseReader,
  reporter: StrengthTestingProblemReporter,
  watcher: unknown,
): Promise<StrengthTestingLoadResult> {
  try {
    return await readAllTestExercisesAsync(testingSlots, reader, reporter, watcher);
  } catch (err) {
    if (!(err instanceof StrengthTestReadFailed)) {
      throw err;
    }
    // reported here, once, as what it is. The first failure ends the load, so a user who is
    // offline waits for two reads, not two for every exercise.
    reporter.trackSilentProblem('StrengthTestExerciseReadFailed', {
      exerciseId: err.exerciseId,
      error: describeErrorForLog(err.reason),
    });
    return { status: StrengthTestsLoad.ReadFailed, slots: [], exercises: [] };
  }
}

/** the reading itself; throws StrengthTestReadFailed at the first exercise that cannot be read */
async function readAllTestExercisesAsync(
  testingSlots: ExerciseSlot[],
  reader: StrengthTestingExerciseReader,
  reporter: StrengthTestingProblemReporter,
  watcher: unknown,
): Promise<StrengthTestingLoadResult> {
  const uiSlots: StrengthTestingUI_Slot[] = [];
  const exercises: Exercise[] = [];

  for (const slot of testingSlots) {
    const sts: StrengthTestingUI_Slot = {
      idSlot: slot._id,
      slotName: slot.slotName,
      slot: slot,
      exercises: [],
    };
    uiSlots.push(sts);

    for (const entry of slot.testExercises) {
      const e = await readWithRetryAsync(reader, entry.exerciseId, watcher);
      if (!e) {
        reporter.trackSilentProblem(`Slot ${slot._id} refers to nonexisting exercise ${entry.exerciseId}`, {});
        continue;
      }
      if (e.standard) {
        sts.exercises.push({ exercise: e });
        exercises.push(e);
        // if the standard is relative to a different exercise
        if (isRelativeStandard(e.standard)) {
          // load the other exercise too
          const otherExercise = await readWithRetryAsync(reader, e.standard.idReferenceExercise, watcher);
          if (otherExercise) {
            exercises.push(otherExercise);
          }
        }
      }
    }
  }

  return {
    status: StrengthTestsLoad.Loaded,
    // remove those slots that don't have any exercises in the end
    slots: uiSlots.filter(s => s.exercises.length > 0),
    exercises,
  };
}

/**
 * Bring the user's saved strength answers in line with the slots the step shows: drop a
 * slot the step no longer has, add one it newly has, and reset a slot whose chosen
 * exercise the slot no longer lists. Changes `declared` in place.
 */
export function reconcileDeclaredStrength(declared: DeclaredStrength, uiSlots: StrengthTestingUI_Slot[]) {
  // synchronize the current (potentially empty) data in the user's state with the available templates...
  // remove nonexisting
  declared.slots = declared.slots.filter(s => uiSlots.find(s2 => s2.idSlot == s.idSlot));

  // add missing
  uiSlots.forEach(s2 => {
    if (!declared.slots.find(s => s.idSlot == s2.idSlot)) {
      declared.slots.push({
        idSlot: s2.idSlot,
        idExercise: s2.exercises[0].exercise._id,
        trainingLevel: undefined,
        stats: {},
      });
    }
  });

  // now recheck current exercises in the user's state
  for (let i = 0; i < declared.slots.length; i++) {
    const slot = declared.slots[i];

    // Ensure stats is initialized for existing slots
    if (!slot.stats) {
      slot.stats = {};
    }

    const templateSlot = uiSlots.find(s => s.idSlot == slot.idSlot);
    if (!templateSlot) {
      // this shouldn't be possible due to above syncing, but just in case..
      console.error(`Removing curiously nonexisting slot ${slot.idSlot} from user state.`);
      declared.slots.splice(i, 1);
      i--;
      continue;
    }
    // if the user's exercise is uninitialized, that's ok
    if (!slot.idExercise) {
      // but if the exercise isn't found in this slot anymore
    } else if (!templateSlot.exercises.find(e => e.exercise._id == slot.idExercise)) {
      // reset this slot for the user
      const templateExercise = templateSlot.exercises[0];
      declared.slots[i] = {
        idSlot: templateSlot.idSlot,
        idExercise: templateExercise.exercise._id,
        stats: {},
      };
    }
  }
}
